import { useState, useCallback } from 'react';
import {
  Box,
  Stack,
  Button,
  Alert,
  CircularProgress,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { format, parseISO } from 'date-fns';
import { CondensedPotSummary } from '../CondensedPotSummary';
import { useRejectPot } from '../../hooks/useRejectPot';
import {
  useResolveRejectionReason,
  useRemoveRejectionReason,
} from '../../hooks/useRejectionReasons';
import type { PotDetailsDisplayModel } from '../../types/potDetails.types';

interface PotRejectionPanelContentProps {
  potDetails: PotDetailsDisplayModel;
  onSuccess: () => void;
}

// Reason can be existing (with id) or new (just text)
interface ReasonItem {
  id?: string; // Present for existing reasons
  reason: string;
}

export function PotRejectionPanelContent({
  potDetails,
  onSuccess,
}: PotRejectionPanelContentProps) {
  // Pre-populate with existing unresolved rejection reasons
  const [reasons, setReasons] = useState<ReasonItem[]>(() =>
    potDetails.rejectionReasons
      .filter(r => !r.isResolved)
      .map(r => ({ id: r.id, reason: r.reason }))
  );
  const [currentReason, setCurrentReason] = useState('');

  const { mutate: rejectPot, isPending: isRejecting } = useRejectPot();

  const { mutate: resolveReason, isPending: isResolving } =
    useResolveRejectionReason();

  const { mutate: removeReason, isPending: isRemoving } =
    useRemoveRejectionReason();

  const isPending = isRejecting || isResolving || isRemoving;

  const handleAddReason = useCallback(() => {
    const trimmedReason = currentReason.trim();
    if (trimmedReason && reasons.length < 20) {
      setReasons(prev => [...prev, { reason: trimmedReason }]);
      setCurrentReason('');
    }
  }, [currentReason, reasons.length]);

  const handleRemoveReason = useCallback(
    (index: number) => {
      const reasonToRemove = reasons[index];
      if (reasonToRemove.id) {
        // Existing reason - call API to remove
        removeReason(
          { potId: potDetails.id, reasonId: reasonToRemove.id },
          {
            onSuccess: () => {
              setReasons(prev => prev.filter((_, i) => i !== index));
            },
          }
        );
      } else {
        // New reason - just remove from local state
        setReasons(prev => prev.filter((_, i) => i !== index));
      }
    },
    [reasons, potDetails.id, removeReason]
  );

  const handleResolveReason = useCallback(
    (index: number) => {
      const reasonToResolve = reasons[index];
      if (reasonToResolve.id) {
        resolveReason(
          { potId: potDetails.id, reasonId: reasonToResolve.id },
          {
            onSuccess: () => {
              setReasons(prev => prev.filter((_, i) => i !== index));
            },
          }
        );
      }
    },
    [reasons, potDetails.id, resolveReason]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleAddReason();
      }
    },
    [handleAddReason]
  );

  const handleConfirmRejection = () => {
    // Get only new reasons (without IDs) to send to the API
    const newReasons = reasons.filter(r => !r.id).map(r => r.reason);

    rejectPot(
      {
        potId: potDetails.id,
        reasons: newReasons,
      },
      {
        onSuccess: () => onSuccess(),
      }
    );
  };

  const canAddReason = currentReason.trim().length > 0 && reasons.length < 20;
  const canReject = reasons.length > 0;
  const newReasonsCount = reasons.filter(r => !r.id).length;

  // Get resolved rejection reasons
  const resolvedRejections = potDetails.rejectionReasons.filter(
    r => r.isResolved
  );

  const formatDateTime = (dateString: string) => {
    return format(parseISO(dateString), 'dd MMM yyyy, HH:mm');
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {/* Condensed Pot Summary with rejection form after financials */}
          <CondensedPotSummary
            potDetails={potDetails}
            showDeductions={true}
            renderAfterFinancials={
              <Stack spacing={2}>
                {/* Add Reason Input */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Rejection Reasons
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter a rejection reason..."
                      value={currentReason}
                      onChange={e => setCurrentReason(e.target.value)}
                      onKeyDown={handleKeyDown}
                      slotProps={{
                        htmlInput: {
                          maxLength: 1000,
                          'data-testid': 'rejection-reason-input',
                        },
                      }}
                      disabled={isPending}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddReason}
                      disabled={!canAddReason || isPending}
                      sx={{ minWidth: 'auto', px: 2 }}
                      data-testid="add-rejection-reason-button"
                    >
                      <AddIcon />
                    </Button>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    Press Enter or click + to add. Maximum 20 reasons.
                  </Typography>
                </Box>

                {/* Reasons List */}
                {reasons.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Reasons ({reasons.length})
                    </Typography>
                    <List
                      dense
                      sx={{ bgcolor: 'action.hover', borderRadius: 1 }}
                    >
                      {reasons.map((item, index) => (
                        <ListItem key={item.id ?? `new-${index}`}>
                          <ListItemText
                            primary={item.reason}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                          <ListItemSecondaryAction>
                            <Stack direction="row" spacing={0.5}>
                              {item.id && (
                                <Tooltip title="Mark as resolved">
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    color="success"
                                    onClick={() => handleResolveReason(index)}
                                    disabled={isPending}
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Remove">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveReason(index)}
                                  disabled={isPending}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Resolved Reasons Card */}
                {resolvedRejections.length > 0 && (
                  <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        gutterBottom
                        color="text.secondary"
                      >
                        Resolved Reasons ({resolvedRejections.length})
                      </Typography>
                      <List
                        dense
                        sx={{ bgcolor: 'action.hover', borderRadius: 1 }}
                      >
                        {resolvedRejections.map(reason => (
                          <ListItem
                            key={reason.id}
                            sx={{
                              textDecoration: 'line-through',
                              opacity: 0.7,
                            }}
                          >
                            <ListItemText
                              primary={reason.reason}
                              secondary={
                                <Stack component="span" spacing={0.5}>
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Added by {reason.createdByName}
                                  </Typography>
                                  {reason.resolvedAt && (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="success.main"
                                    >
                                      Resolved by {reason.resolvedByName} on{' '}
                                      {formatDateTime(reason.resolvedAt)}
                                    </Typography>
                                  )}
                                </Stack>
                              }
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: { textDecoration: 'line-through' },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            }
          />
        </Stack>
      </Box>

      {/* Fixed bottom action area */}
      <Box
        sx={{
          p: 3,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        {/* Helper text */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          Rejecting this pot will notify the owner. They will need to address
          all rejection reasons before resubmitting.
        </Alert>

        <Button
          variant="contained"
          color="error"
          size="large"
          fullWidth
          startIcon={
            isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CancelIcon />
            )
          }
          onClick={handleConfirmRejection}
          disabled={!canReject || isPending}
          data-testid="confirm-rejection-button"
        >
          {isPending
            ? 'Processing...'
            : newReasonsCount > 0
              ? `Reject with ${newReasonsCount} new reason${newReasonsCount !== 1 ? 's' : ''}`
              : `Reject (${reasons.length} existing reason${reasons.length !== 1 ? 's' : ''})`}
        </Button>
      </Box>
    </Box>
  );
}
